"use client";

import { Button } from "@/shadcn/ui/button";
import { EyeOff } from "lucide-react";
import { cn } from "./utils";
import { CSSProperties, useState } from "react";
import { useHideComedian } from "@/hooks/hide/useHideComedian";
import { useHideSpecial } from "@/hooks/hide/useHideSpecial";
import { trackEvent } from "@/analytics";

interface Props {
  className?: string;
  specialId?: string;
  comedianId?: string;
  apiEndpoint?: string;
  onHide?: (postId: string) => void;
  postId?: string;
  location?: string;
  label?: string;
  iconColor?: string;
  iconWidth?: number;
  iconHeight?: number;
  classicButton?: boolean;
  gap?: number;
  style?: CSSProperties;
  analyticsEvent?: string;
  analyticsData?: Record<string, any>;
}

export default function HideButton({
  className,
  specialId,
  comedianId,
  apiEndpoint,
  onHide,
  postId,
  location,
  label,
  iconColor = "stroke-[#3F3F3F]",
  iconWidth = 18,
  iconHeight = 18,
  classicButton = false,
  gap,
  style,
  analyticsEvent,
  analyticsData,
}: Props) {
  const { hideComedian, isHiding: isHidingComedian } = useHideComedian();
  const { hideSpecial, isHiding: isHidingSpecial } = useHideSpecial();
  const [isHidingCustom, setIsHidingCustom] = useState(false);

  const isHiding = isHidingComedian || isHidingSpecial || isHidingCustom;

  const handleHideClick = async () => {
    if (isHiding) return;

    if (apiEndpoint) {
      setIsHidingCustom(true);
      try {
        const res = await fetch(apiEndpoint, {
          method: "POST",
        });
        if (res.ok) {
          const eventName =
            analyticsEvent ||
            (comedianId ? "FEED_HIDE_COMEDIAN" : "FEED_HIDE_SPECIAL");
          const eventData =
            analyticsData ||
            (comedianId
              ? { comedian_id: comedianId, location }
              : { special_id: specialId, location });

          trackEvent(eventName, eventData);
          if (postId && onHide) {
            onHide(postId);
          }
        }
      } catch (err) {
        console.error("Failed to hide", err);
      } finally {
        setIsHidingCustom(false);
      }
      return;
    }

    if (comedianId) {
      hideComedian({
        comedianId,
        location,
        onHide: onHide && postId ? () => onHide(postId) : undefined,
      });
    } else if (specialId) {
      hideSpecial({
        specialId,
        postId,
        location,
        onHide,
      });
    } else {
      console.error(
        "HideButton: missing specialId, comedianId, or apiEndpoint"
      );
    }
  };

  return (
    <>
      {classicButton ? (
        <Button
          variant="punchup"
          onClick={handleHideClick}
          className={className}
          style={style}
          disabled={isHiding}
        >
          <EyeOff className="w-4 h-4 mr-2" />
          {label || "Hide"}
        </Button>
      ) : (
        <button
          type="button"
          title="hide"
          className={cn(
            "p-2 transition-colors duration-200 ease-in-out hover:bg-gray-50 border border-transparent hover:border-slate-200 active:border-slate-300 rounded-md text-[13px] md:text-[15px]",
            "text-primary-blue stroke-primary-blue",
            className
          )}
          onClick={handleHideClick}
          style={style}
          disabled={isHiding}
        >
          <div
            className={cn(
              "flex flex-row justify-center items-center",
              !gap && "gap-x-1.5 md:gap-x-2"
            )}
            style={{
              gap,
            }}
          >
            <EyeOff
              className={cn(
                "transition-colors duration-200 ease-in-out stroke-inherit",
                iconColor
              )}
              width={iconWidth}
              height={iconHeight}
            />
            {label && (
              <span className="font-semibold leading-none text-inherit">
                {label}
              </span>
            )}
          </div>
        </button>
      )}
    </>
  );
}
